import React, {PureComponent} from "react";
import {GameEntity} from "../data/entities/GameEntity";
import GamesList from "./GamesList";
import {FormattedMessage} from "react-intl";
import FeaturedNews from "./FeaturedNews";
import GamesFilters from "./GamesFilters";
import {FiltersEntity} from "../data/entities/FiltersEntity";
import classNames from 'classnames';
import _ from "underscore";
import ProgressionLevels from "./ProgressionLevels";
import PlayerProgressionStatus from "./PlayerProgressionStatus";
import CustomerOffersList from "../containers/Rewards/CustomerOffersList";
import {LocaleContext} from "../context/LocaleContext";
import {UserContext} from "../context/UserContext";
import ProgressionProvider, {ProgressionContext} from "../context/ProgressionContext";
import {NewsItemEntity} from "../data/entities/NewsItemEntity";
import popularGames from "../data/PopularGames";
import removeEmptyElements from "../utils/typescript";
import {subscriptionTypes} from "../data/entities/UserSubscriptionInfoEntity";
import GamePlayedEntity from "../data/entities/GamePlayedEntity";
import AdobeAnalyticsProvider, {AdobeAnalyticsContextData} from "../context/AdobeAnalyticsContext";

interface Props {
    games: GameEntity[];
    news: NewsItemEntity[];
    displayedGames: GameEntity[];
    gamesPlayed: GamePlayedEntity[];
    // recommendedGames: RecommendedGameEntity[];
    filters: FiltersEntity;
    hasGameFilters: boolean;
    order: "name" | "launch date";
    displayMode: "list" | "grid";
    setNameFilter: Function;
    setDisplayMode: (value: "list" | "grid") => void;
    setOrder: (value: "name" | "launch date") => void;
    toggleBrandFilter: Function;
    handleGameAction: Function;
    analytics: AdobeAnalyticsContextData;
    locale: string;
}

export default class Home extends PureComponent<Props> {
    static contextType = UserContext;
    context!: React.ContextType<typeof UserContext>;

    private visibleActivatedGames = 7;

    private gameOwnershipIds = [1, 2];

    componentDidMount() {
        this.props.analytics.track("pageevent", {
            pageName: "main",
            siteSection: "homepage",
            language: AdobeAnalyticsProvider.getLocaleLanguage(this.props.locale),
            country: AdobeAnalyticsProvider.getLocaleCountry(this.props.locale)
        });
    }

    render(): JSX.Element {
        const gameListClassNames = classNames({
            gamelist: true,
            list: this.props.displayMode === "list"
        });

        const {hasGameFilters} = this.props;

        const brands = _.unique(_.compact(this.props.games.map(game => game.brand))).sort();

        const newsItems: NewsItemEntity[] = this.props.news.map(newsItem => ({
            ...newsItem,
            game: this.props.games.find(game => game.uplayGameId === newsItem.obj.uplayGameId)
        }));

        const displayedPopularGames: GameEntity[] = popularGames
            .map(popularGame => (
                this.props.games.find(game => popularGame.uplayGameIds.includes(game.uplayGameId))
            ))
            .filter(removeEmptyElements);

        const userOwnership = (game: GameEntity): boolean => this.gameOwnershipIds.includes(game.ownership);
        let ownedGames: GameEntity[] = this.props.games.filter(userOwnership);

        let gamesPlayedLength = this.props.gamesPlayed.length;
        let ownedGamesLength = ownedGames.length;
        if (gamesPlayedLength > 0) {
            let maxCount = (ownedGamesLength > gamesPlayedLength) ? ownedGamesLength : gamesPlayedLength;
            ownedGames = _.sortBy(ownedGames, game => {
                let index = _.findIndex(this.props.gamesPlayed, {spaceId: game.spaceId});
                if (index < 0) {
                    return maxCount;
                }
                return index;
            })
        }

        // const recommendedGames: GameEntity[] = this.props.recommendedGames
        //     .map(recommendedGame => (
        //         this.props.games.find(game => {
        //             return (
        //                 game.uplayGameId === parseInt(recommendedGame.id) &&
        //                 !this.gameOwnershipIds.includes(game.initialOwnership)
        //             );
        //         })
        //     ))
        //     .filter(removeEmptyElements)
        //     .slice(0, 7);

        return (
            <LocaleContext.Consumer>
                {({locale}) => (
                    <ProgressionProvider
                        userProgression={this.context.userProgression}
                        validateUserCountry={this.context.validateUserCountry}
                        validateProgressionType={this.context.validateProgressionType}
                        validateSubscriptionProgressionPrivilege={this.context.validateSubscriptionProgressionPrivilege}
                        locale={locale}
                        ticketData={this.context.user.ticketData}
                    >
                        <>
                            <ProgressionContext.Consumer>
                                {({
                                      displayProgression,
                                      isLoadingProgressionLevels,
                                      progressionLevels,
                                      hasErrorProgressionLevels,
                                      refreshProgressionData
                                  }) => (
                                    displayProgression && this.context.userProgression && this.context.userProgression.currentProgressionLevel &&
                                    <React.Fragment>
                                        <PlayerProgressionStatus
                                            userProgressionInfo={this.context.userProgression}
                                            progressionLevels={progressionLevels}
                                            isLoadingProgressionWidget={isLoadingProgressionLevels}
                                            hasError={hasErrorProgressionLevels}
                                            refreshProgressionData={refreshProgressionData}
                                        />
                                    </React.Fragment>
                                )}
                            </ProgressionContext.Consumer>

                            <FeaturedNews
                                news={newsItems}
                                handleGameAction={this.props.handleGameAction}
                                userProgression={this.context.userProgression}
                            />

                            {
                                this.context.validateUserCountry(process.env.REACT_APP_COUNTRIES_REWARDS) &&
                                this.context.validateProgressionType([subscriptionTypes.premium, subscriptionTypes.premiumAnywhere]) &&
                                this.context.validateSubscriptionProgressionPrivilege() &&
                                    <CustomerOffersList locale={locale} games={this.props.games}/>
                            }

                            <div className="gamelist-container mb-0" id="nav-anchor-library">
                                <GamesFilters
                                    filters={this.props.filters}
                                    brands={brands}
                                    displayMode={this.props.displayMode}
                                    order={this.props.order}
                                    setNameFilter={this.props.setNameFilter}
                                    toggleBrandFilter={this.props.toggleBrandFilter}
                                    setOrder={this.props.setOrder}
                                    setDisplayMode={this.props.setDisplayMode}
                                />

                                {
                                    !hasGameFilters && ownedGamesLength > 0 &&
                                    <>
                                        <h3 className="section-title">
                                            <FormattedMessage id="heading.my_games" tagName="span"/>
                                        </h3>
                                        <div className={gameListClassNames}>
                                            <GamesList
                                                games={ownedGames}
                                                handleGameAction={this.props.handleGameAction}
                                                itemsPerPage={this.visibleActivatedGames}
                                                userOwnership={userOwnership}
                                            />
                                        </div>
                                    </>
                                }

                                {
                                    !hasGameFilters && displayedPopularGames.length > 0 &&
                                    <>
                                        <h3 className="section-title">
                                            <FormattedMessage id="heading.popular_games" tagName="span"/>
                                        </h3>
                                        <div className={gameListClassNames}>
                                            <GamesList
                                                games={displayedPopularGames}
                                                handleGameAction={this.props.handleGameAction}
                                                userOwnership={userOwnership}
                                            />
                                        </div>
                                    </>
                                }

                                {/*{*/}
                                {/*    !hasGameFilters && recommendedGames.length > 0 &&*/}
                                {/*    <>*/}
                                {/*        <h3 className="section-title mt-5">*/}
                                {/*            <FormattedMessage id="heading.recommended_games" tagName="span"/>*/}
                                {/*        </h3>*/}
                                {/*        <div className={gameListClassNames}>*/}
                                {/*            <GamesList*/}
                                {/*                games={recommendedGames}*/}
                                {/*                handleGameAction={this.props.handleGameAction}*/}
                                {/*                userOwnership={userOwnership}*/}
                                {/*            />*/}
                                {/*        </div>*/}
                                {/*    </>*/}
                                {/*}*/}

                                <div id="nav-anchor-all-games">

                                    <h2>
                                        <FormattedMessage id="heading.all_games"/>
                                    </h2>

                                    <div className={gameListClassNames}>
                                        {
                                            this.props.displayedGames.length === 0 &&
                                            <p className={"notice"}><FormattedMessage id="message.no_available_games"/></p>
                                        }

                                        {
                                            this.props.displayedGames.length > 0 &&
                                            <GamesList
                                                games={this.props.displayedGames}
                                                handleGameAction={this.props.handleGameAction}
                                                userOwnership={userOwnership}
                                            />
                                        }
                                    </div>
                                </div>
                            </div>

                            <ProgressionContext.Consumer>
                                {({
                                      displayProgression,
                                      isLoadingProgressionLevels,
                                      progressionLevels,
                                      hasErrorProgressionLevels,
                                      refreshProgressionData,
                                  }) => (
                                    displayProgression && this.context.userProgression && this.context.userProgression.currentProgressionLevel &&
                                    <ProgressionLevels
                                        progressionLevels={progressionLevels}
                                        hasError={hasErrorProgressionLevels}
                                        refreshProgressionData={refreshProgressionData}
                                        userProgressionInfo={this.context.userProgression}
                                        isLoadingProgressionLevels={isLoadingProgressionLevels}
                                    />
                                )}
                            </ProgressionContext.Consumer>
                        </>
                    </ProgressionProvider>

                )}
            </LocaleContext.Consumer>
        )
    }
}